import styled from '@emotion/styled';
import { codeHighlightTokensCss } from 'app/codeHighlight';
import { MONO_FONT } from 'ts-components/PiChat/styles';

const SELECTED_BG =
  'color-mix(in srgb, var(--color-button-secondary-default) 10%, transparent)';

export const FilesSection = styled.div`
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  min-width: 0;
  border-bottom: 1px solid var(--color-border-medium-light);
`;

export const DisclosureButton = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  width: 100%;
  min-width: 0;
  padding: 8px 15px;
  border: none;
  background: transparent;
  text-align: left;
  cursor: pointer;
  color: var(--color-font-primary);

  &:hover {
    background: var(--color-background-hover);
  }

  &:disabled {
    cursor: default;
    background: transparent;
  }

  &:focus:not(:focus-visible) {
    outline: none;
  }
`;

export const Chevron = styled.span<{ $expanded: boolean }>`
  display: inline-flex;
  flex-shrink: 0;
  width: 6px;
  height: 6px;
  margin: 0 3px;
  border-right: 1.5px solid currentColor;
  border-bottom: 1.5px solid currentColor;
  color: var(--color-font-placeholder);
  transform: ${({ $expanded }) =>
    $expanded
      ? 'translateY(-2px) rotate(45deg)'
      : 'translateX(-1px) rotate(-45deg)'};
`;

export const SplitPane = styled.div`
  display: flex;
  min-width: 0;
  height: min(280px, 34vh);
  border-top: 1px solid var(--color-border-medium-light);
`;

export const TreePane = styled.div`
  flex: 0 0 150px;
  display: flex;
  flex-direction: column;
  min-width: 0;
  border-right: 1px solid var(--color-border-medium-light);
`;

export const PaneFrame = styled.div`
  position: relative;
  flex: 1;
  min-height: 0;
  min-width: 0;
  display: flex;
  flex-direction: column;
`;

export const TreeScroll = styled.div`
  flex: 1;
  min-height: 0;
  overflow: auto;
  padding: 6px 4px;
`;

export const TreeList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
`;

export const TreeRoot = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  height: 24px;
  padding: 0 6px;
  font-size: 12px;
  font-weight: 600;
  color: var(--color-font-secondary);
  min-width: 0;
`;

export const TreeRowButton = styled.button<{
  $depth: number;
  $selected?: boolean;
}>`
  display: flex;
  align-items: center;
  gap: 6px;
  width: 100%;
  min-width: 0;
  height: 24px;
  border: none;
  border-radius: 4px;
  background: ${({ $selected }) => ($selected ? SELECTED_BG : 'transparent')};
  color: ${({ $selected }) =>
    $selected
      ? 'var(--color-button-secondary-default)'
      : 'var(--color-font-primary)'};
  padding: 0 6px 0 ${({ $depth }) => 6 + $depth * 12}px;
  text-align: left;
  font-size: 12px;
  cursor: pointer;

  &:hover {
    background: ${({ $selected }) =>
      $selected ? SELECTED_BG : 'var(--color-background-hover)'};
  }

  &:focus:not(:focus-visible) {
    outline: none;
  }
`;

export const TreeRowLabel = styled.span`
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const CodePaneRoot = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  min-width: 0;
  background: var(--color-code-highlight-surface);
`;

export const CodeViewerHeader = styled.div`
  flex-shrink: 0;
  min-width: 0;
  padding: 4px 10px;
  border-bottom: 1px solid var(--color-border-medium-light);
  font-family: ${MONO_FONT};
  font-size: 11px;
  line-height: 16px;
  color: var(--color-font-secondary);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const CodeScroll = styled.div`
  flex: 1;
  min-height: 0;
  display: flex;
  align-items: flex-start;
  overflow: auto;
  font-family: ${MONO_FONT};
  font-size: 11px;
  line-height: 16px;
`;

export const LineGutter = styled.pre`
  position: sticky;
  left: 0;
  z-index: 1;
  flex-shrink: 0;
  margin: 0;
  overflow: visible;
  padding: 8px 8px 8px 10px;
  min-height: 100%;
  text-align: right;
  font: inherit;
  color: var(--color-code-highlight-comment);
  background: var(--color-code-highlight-surface);
  user-select: none;
`;

export const CodeBlock = styled.pre`
  flex: 1 0 auto;
  margin: 0;
  overflow: visible;
  padding: 8px 10px 8px 4px;
  font: inherit;
  white-space: pre;
  tab-size: 2;
  color: var(--color-code-highlight-text);

  ${codeHighlightTokensCss}
`;
